import * as React from 'react';
import { getSupabase } from '@/lib/supabase';
import { invitesStore } from '@/data/storage/app/invites.adapter';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { AuthDivider } from '@/components/auth/AuthDivider';
import { AuthShell } from '@/components/auth/AuthShell';
import { syncSessionToServer } from '@/utils/syncSession';
import { savePendingInvite } from '@/utils/pendingInvite';

export default function Signup() {
  const search = new URLSearchParams(window.location.search);
  const inviteId = search.get('invite') || '';
  const inviteToken = search.get('token') || '';
  const emailParam = search.get('email') || '';
  const errorParam = search.get('error') || '';
  const onboardingReturnTarget = inviteId
    ? `/onboarding?invite=${encodeURIComponent(inviteId)}`
    : inviteToken && emailParam
      ? `/onboarding?token=${encodeURIComponent(inviteToken)}&email=${encodeURIComponent(emailParam)}`
      : '/';

  const initialErrorMessage =
    errorParam === 'invite_email_mismatch'
      ? 'This invite was sent to a different email address. Please sign in with the email your coach invited.'
      : '';

  const [email, setEmail] = React.useState(emailParam);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(initialErrorMessage);
  const [success, setSuccess] = React.useState(false);
  const [showPasswordForm, setShowPasswordForm] = React.useState(false);
  const panelClass = 'space-y-4 rounded-3xl border border-border bg-card p-6 shadow-xl shadow-black/5';
  const inputClass =
    'w-full rounded-xl border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20';
  const primaryButtonClass =
    'w-full rounded-xl bg-emerald-600 px-4 py-3 font-medium text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50';
  const legalFooter = (
    <p className="text-center text-xs text-muted-foreground">
      By continuing you agree to our{' '}
      <a
        href="https://os-coach.com/terms"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-foreground underline underline-offset-2"
      >
        Terms of Service
      </a>{' '}
      and{' '}
      <a
        href="https://os-coach.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-foreground underline underline-offset-2"
      >
        Privacy Policy
      </a>
      .
    </p>
  );

  // Strip the one-shot error param from the URL once we've surfaced it so a
  // refresh doesn't keep showing it.
  React.useEffect(() => {
    if (!errorParam) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    window.history.replaceState({}, '', url.toString());
  }, [errorParam]);

  // Fetch email from invite if not in URL
  React.useEffect(() => {
    (async () => {
      if (!email && inviteId) {
        const inv = await invitesStore.get(inviteId);
        if (inv?.email) setEmail(inv.email);
      }
    })();
  }, [inviteId, email]);

  // Google OAuth signup (invite required)
  const handleGoogleSignup = async () => {
    if (!inviteId && !inviteToken) {
      setError('Invalid or missing invite link. Ask your coach to resend it.');
      return;
    }
    const sb = getSupabase();
    if (!sb) {
      setError('Authentication not configured');
      return;
    }
    savePendingInvite({
      inviteId: inviteId || undefined,
      token: inviteToken || undefined,
      email: emailParam || undefined,
    });
    setLoading(true);
    try {
      // Redirect to Worker-initiated OAuth flow with invite param
      const params = new URLSearchParams({ provider: 'google' });
      if (inviteId) params.set('invite', inviteId);
      if (inviteToken) params.set('token', inviteToken);
      if (emailParam) params.set('email', emailParam);
      window.location.href = `/api/auth/start?${params.toString()}`;
    } catch (err: any) {
      setError(err?.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const sb = getSupabase();
    if (!sb) {
      setError('Authentication not configured');
      return;
    }

    setLoading(true);
    try {
      const origin = window.location.origin;
      const callbackParams = new URLSearchParams();
      if (inviteId) callbackParams.set('invite', inviteId);
      if (inviteToken) callbackParams.set('token', inviteToken);
      if (emailParam) callbackParams.set('email', emailParam);
      const callbackQuery = callbackParams.toString();
      const emailRedirectTo = `${origin}/auth/callback${callbackQuery ? `?${callbackQuery}` : ''}`;

      const { data, error: signupError } = await sb.auth.signUp({
        email: email.trim(),
        password: password,
        options: {
          emailRedirectTo,
          data: {
            role: 'client',
            invite_id: inviteId || undefined,
            invite_token: inviteToken || undefined,
          },
        },
      });

      if (signupError) throw signupError;

      // Check if email confirmation is required
      if (data.user && !data.session) {
        setSuccess(true);
        setError('confirm-email');
      } else if (data.session) {
        // Await session-sync completion BEFORE redirect to avoid a cookie
        // race where the first post-redirect Worker call arrives anonymous.
        const synced = await syncSessionToServer({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
        if (!synced) {
          setError('Account created but we could not sync your session. Please sign in.');
          return;
        }
        setSuccess(true);
        setTimeout(() => {
          window.location.replace(onboardingReturnTarget);
        }, 1000);
      }
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      role="invite"
      title="Complete your client account"
      subtitle="Use the invite from your coach to finish setting up your client portal."
      footer={legalFooter}
    >
      {success ? (
        <div className="space-y-4">
          {error === 'confirm-email' ? (
            <div className="space-y-4 rounded-3xl border border-border bg-card p-6 text-center shadow-xl shadow-black/5">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
                <svg
                  className="h-7 w-7 text-emerald-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Check your email</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  We sent a confirmation link to <span className="font-medium text-foreground">{email}</span>. Click the
                  link in the email to verify your account.
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                After confirming,{' '}
                <a
                  href={(() => {
                    const sp = new URLSearchParams();
                    if (onboardingReturnTarget && onboardingReturnTarget !== '/') {
                      sp.set('returnTo', onboardingReturnTarget);
                    }
                    const qs = sp.toString();
                    return `/client/auth${qs ? `?${qs}` : ''}`;
                  })()}
                  className="font-medium text-emerald-600 hover:underline dark:text-emerald-400"
                >
                  sign in here
                </a>{' '}
                to continue.
              </p>
            </div>
          ) : (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
              <p className="text-green-400">Account created successfully! Redirecting...</p>
            </div>
          )}
        </div>
      ) : (
        <div className={panelClass}>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{error}</div>
          )}

          {/* Google OAuth primary path */}
          <GoogleSignInButton onSignIn={handleGoogleSignup} loading={loading} text="Continue with Google" />
          <AuthDivider />

          {/* Collapsible password fallback */}
          <button
            type="button"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Or continue with password
          </button>

          {showPasswordForm && (
            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                  required
                  disabled={!!emailParam}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  placeholder="Re-enter your password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || !email.trim() || !password || !confirmPassword}
                className={primaryButtonClass}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <a href="/client/auth" className="font-medium text-emerald-600 hover:underline dark:text-emerald-400">
              Sign In
            </a>
          </p>
        </div>
      )}
    </AuthShell>
  );
}
